/**
 * What the payments-configuration screen needs to know about each payment provider, in one place: the
 * methods it may be selected for, how it is labelled, and — for a provider that keeps a
 * `payment_provider_specific` document — the initial-values default, the normalisation applied on the
 * way in and out, the Yup rules and the fields component that document needs.
 *
 * Stripe declares only the first two. It keeps no per-method configuration of its own (nothing under
 * `server/modules/stripe` reads `payment_provider_specific`); its credentials are configured once for
 * the organisation on the integrations screen.
 *
 * The generic form and the provider select read every provider-specific rule through this map and name
 * no provider themselves, so adding a provider is adding an entry here — and a provider that declares
 * no rules cannot inherit another's.
 */

import type { ComponentType } from 'react';
import type * as Yup from 'yup';

import PAYMENT_METHODS_SUPPORTED_BY_STRIPE from '~/server_shared/generated-types/PAYMENT_METHODS_SUPPORTED_BY_STRIPE';

import {
  getVitesseDefaultProviderSpecificConfig,
  getVitesseProviderSpecificValidationShape,
  withVitesseVerificationDefaults,
  withVitesseVerificationOutgoingValues,
} from './VitesseProviderConfig/verificationConfig';
import type { PaymentMethodConfigurationValues, ProviderSpecificConfig } from './types';
import VitesseProviderFields from './VitesseProviderConfig';

/** What every provider fields component is handed. A provider that renders nothing declares no `Fields`. */
export interface ProviderFieldsProps {
  currentPaymentMethod: string;
  isCustomMethod: boolean;
  readOnly?: boolean;
}

interface MethodContext {
  paymentMethodName: string;
  isCustomMethod: boolean;
}

/** What a provider's validation rules are given, on top of the method context. */
interface ValidationContext extends MethodContext {
  /**
   * Whether the fund-account engine resolves this method's account per payment. A provider that keeps a
   * fund account has none to ask for when it does, and the server drops its own requirement on the same
   * condition — so a rule that stayed would block a save the backend accepts.
   */
  isFundAccountSourceRules: boolean;
}

export interface PaymentProviderDescriptor {
  displayName: string;
  /** The methods this provider may be selected for on this screen. */
  supportedMethods: readonly string[];
  /**
   * `required_fields` this provider forces on (checked and locked) while it is selected, on top of the
   * method-intrinsic ones every provider gets.
   */
  getForcedRequiredFields?: (context: MethodContext) => string[];
  /**
   * A `merge()` layer for the form's initial values, so a persisted value still wins over the default.
   * Shaped as the nested document rather than the inner block for exactly that reason.
   */
  getDefaultProviderSpecific?: (context: { paymentMethodName: string; paymentProvider?: string | null }) => {
    method_configuration?: { payment_provider_specific: ProviderSpecificConfig };
  };
  /**
   * Normalisation applied to the loaded `payment_provider_specific` before the form holds it. May return
   * the document untouched — including an explicit `null`, which a saved row is allowed to hold.
   */
  withIncomingDefaults?: (
    paymentProviderSpecific: ProviderSpecificConfig | null | undefined,
    context: MethodContext
  ) => ProviderSpecificConfig | null | undefined;
  /** Normalisation applied to the whole set of form values on submit. */
  withOutgoingValues?: (
    values: PaymentMethodConfigurationValues,
    context: MethodContext
  ) => PaymentMethodConfigurationValues;
  /** Rules the generic schema spreads into `payment_provider_specific` while this provider is selected. */
  getValidationShape?: (context: ValidationContext) => Record<string, Yup.AnySchema>;
  Fields?: ComponentType<ProviderFieldsProps>;
}

/**
 * A descriptor's functions are only ever reached through the entry they are registered under — the form
 * and the select look the descriptor up by the *selected* provider before calling anything on it — so a
 * provider's helpers may take the document in the shape they wrote it, and the registry stays opaque.
 */
const PAYMENT_PROVIDERS_CONFIG: Record<string, PaymentProviderDescriptor> = {
  vitesse: {
    displayName: 'Vitesse',
    // Which methods this screen offers Vitesse for. Kept here rather than in `enums.json`: unlike
    // Stripe's list — which the server reads too, in `payments_config_manager` — the server has no
    // single Vitesse counterpart to stay in step with. It states the rule per method instead (the
    // Payment-Requests-only set in `vitesse.config_validation`), so this list is the screen's own.
    supportedMethods: [
      'uk_bank_transfer',
      'au_bank_transfer',
      'nz_bank_transfer',
      'custom_method',
      'check',
      'echeck',
      'us_ach',
    ],
    // A payee choosing how to be paid is contacted to do it, so both are needed before the link is sent.
    getForcedRequiredFields: ({ isCustomMethod }) => (isCustomMethod ? ['phone_number', 'email'] : []),
    getDefaultProviderSpecific: getVitesseDefaultProviderSpecificConfig,
    // Only a payee-choice method carries the verification shape, so anything else is left exactly as it
    // was loaded — an explicit `null` included.
    withIncomingDefaults: (paymentProviderSpecific, { isCustomMethod }) =>
      isCustomMethod ? withVitesseVerificationDefaults(paymentProviderSpecific) : paymentProviderSpecific,
    withOutgoingValues: withVitesseVerificationOutgoingValues,
    getValidationShape: getVitesseProviderSpecificValidationShape,
    Fields: VitesseProviderFields,
  },
  stripe: {
    displayName: 'Stripe',
    // The generated enum the server's `payments_config_manager` also builds its own set from, so a rail
    // cannot be accepted by the backend while this screen leaves the option unoffered.
    supportedMethods: PAYMENT_METHODS_SUPPORTED_BY_STRIPE,
  },
};

export default PAYMENT_PROVIDERS_CONFIG;
